import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, defer, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class HttpRetryService 
{
  // Sert à vérifier si la première requête à échouer ou non
  private firstAttemptMap = new Map<string, boolean>()
  private autoRetryDelayMs = 2000 

  constructor
  (
    private http: HttpClient, 
    private snackBar: MatSnackBar
  ) {}

  /**
   * Effectue une requête GET avec retry via snackbar si le body est null/undefined
   * @param url L'URL de la requête
   * @param params Paramètres optionnels
   */
  get<T>(url: string, options?: { params?: Record<string, string | number>, context?: HttpContext}): Observable<T> 
  {
    const isFirstAttempt = !this.firstAttemptMap.get(url)
    this.firstAttemptMap.set(url, true)

    const httpParams = options?.params 
      ? new HttpParams({ fromObject: Object.fromEntries(
          Object.entries(options.params).map(([key, value]) => [key, value.toString()])
        )})
      : undefined

    const makeRequest = (): Observable<T> => {
      return defer(() => this.http.get<T>(url, { ...options, params:httpParams })).pipe(
        tap(data => {
          // Si la requête a réussi, on réinitialise le flag
          if (data !== null && data !== undefined) 
          {
            this.firstAttemptMap.delete(url);
          }
        }),
        switchMap(data => {
          if (data !== null && data !== undefined) 
          {
            return [data]
          }

          if (isFirstAttempt) 
          {
            // Première tentative : relance automatique une fois
            return timer(this.autoRetryDelayMs).pipe(
              switchMap(() => this.get<T>(url, options))
            )
          }

          return new Observable<T>(subscriber => {
            const snackRef = this.snackBar.open(
              "Échec lors de la récupération des données.",
              'Relancer',
              { duration: 0 }
            )

            const sub = snackRef.onAction().subscribe(() => {
              sub.unsubscribe()
              // relancer la requête
              this.get<T>(url, options).subscribe({
                next: value => subscriber.next(value),
                error: err => subscriber.error(err),
                complete: () => subscriber.complete()
              })
            })
          })
        })
      )
    }

    return makeRequest()
  }
}